import { useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from './useAuth';
import { useToast } from './ToastContext';
import { MainCouranteContext, MainCouranteMessage } from './MainCouranteContext';

const isBypassMode = import.meta.env.DEV && import.meta.env.VITE_BYPASS_HUB_AUTH === 'true';
const BYPASS_MESSAGES_KEY = 'hub_dev_main_courante';
const LAST_SEEN_KEY_PREFIX = 'hub_main_courante_last_seen_';

function getLastSeen(userId: string): string {
  try {
    return localStorage.getItem(LAST_SEEN_KEY_PREFIX + userId) || new Date(0).toISOString();
  } catch {
    return new Date(0).toISOString();
  }
}

function setLastSeen(userId: string, value: string) {
  try {
    localStorage.setItem(LAST_SEEN_KEY_PREFIX + userId, value);
  } catch {
    // ignore
  }
}

function sortMessages(list: MainCouranteMessage[]) {
  return [...list].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
}

function generateId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function playNotificationSound() {
  try {
    if (typeof window === 'undefined') return;
    const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.08, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.15);
  } catch {
    // ignore
  }
}

export function MainCouranteProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const lastNotifiedAt = useRef<string>(new Date().toISOString());
  const [messages, setMessages] = useState<MainCouranteMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [latestUnreadMessage, setLatestUnreadMessage] = useState<MainCouranteMessage | null>(null);
  const [unreadMessages, setUnreadMessages] = useState<MainCouranteMessage[]>([]);

  const computeUnreadList = useCallback(
    (list: MainCouranteMessage[]) => {
      if (!user) return [];
      const lastSeen = new Date(getLastSeen(user.id)).getTime();
      return list.filter((m) => {
        if (user && m.author_id === user.id) return false;
        return new Date(m.created_at).getTime() > lastSeen;
      });
    },
    [user]
  );

  const refresh = useCallback(async () => {
    setError('');
    try {
      let list: MainCouranteMessage[] = [];

      if (isBypassMode) {
        const stored = localStorage.getItem(BYPASS_MESSAGES_KEY);
        list = stored ? JSON.parse(stored) : [];
      } else {
        const res = await fetch('/api/maincourante-list', { credentials: 'include' });
        if (!res.ok) {
          throw new Error('Erreur lors du chargement de la main courante');
        }
        const data = await res.json();
        list = data.messages || [];
      }

      const sorted = sortMessages(list);
      setMessages(sorted);
      const unread = computeUnreadList(sorted);
      setUnreadCount(unread.length);
      setLatestUnreadMessage(unread[0] || null);
      setUnreadMessages(unread);

      const previousNotified = new Date(lastNotifiedAt.current).getTime();
      const hasNewExternal = sorted.some((m) => {
        if (user && m.author_id === user.id) return false;
        return new Date(m.created_at).getTime() > previousNotified;
      });
      if (hasNewExternal) {
        addToast('Nouveau message dans la Main Courante', 'info');
        playNotificationSound();
        lastNotifiedAt.current = new Date().toISOString();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  }, [computeUnreadList, addToast, user]);

  useEffect(() => {
    if (!user) return;
    refresh();
    const interval = setInterval(refresh, 30000);
    return () => clearInterval(interval);
  }, [user, refresh]);

  const persistBypass = useCallback(
    (list: MainCouranteMessage[]) => {
      const sorted = sortMessages(list);
      localStorage.setItem(BYPASS_MESSAGES_KEY, JSON.stringify(sorted));
      setMessages(sorted);
      const unread = computeUnreadList(sorted);
      setUnreadCount(unread.length);
      setLatestUnreadMessage(unread[0] || null);
      setUnreadMessages(unread);
    },
    [computeUnreadList]
  );

  const createMessage = useCallback(
    async (title: string, description: string) => {
      if (isBypassMode) {
        const now = new Date().toISOString();
        const msg: MainCouranteMessage = {
          id: generateId(),
          author_id: user?.id || 'dev-user',
          author_name: user ? `${user.prenom} ${user.nom}`.trim() : 'Dev User',
          title: title.trim(),
          description: description.trim(),
          created_at: now,
          updated_at: now,
        };
        const stored = localStorage.getItem(BYPASS_MESSAGES_KEY);
        const list = stored ? JSON.parse(stored) : [];
        persistBypass([msg, ...list]);
        return true;
      }

      const res = await fetch('/api/maincourante-create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title, description }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Erreur lors de la création');
        return false;
      }

      addToast('Message publié', 'success');
      await refresh();
      return true;
    },
    [user, refresh, persistBypass, addToast],
  );

  const updateMessage = useCallback(
    async (id: string, title: string, description: string) => {
      if (isBypassMode) {
        const stored = localStorage.getItem(BYPASS_MESSAGES_KEY);
        const list = stored ? JSON.parse(stored) : [];
        const updated = list.map((m) =>
          m.id === id
            ? { ...m, title: title.trim(), description: description.trim(), updated_at: new Date().toISOString() }
            : m
        );
        persistBypass(updated);
        return true;
      }

      const res = await fetch('/api/maincourante-update', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id, title, description }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Erreur lors de la mise à jour');
        return false;
      }

      await refresh();
      return true;
    },
    [refresh, persistBypass]
  );

  const deleteMessage = useCallback(
    async (id: string) => {
      if (isBypassMode) {
        const stored = localStorage.getItem(BYPASS_MESSAGES_KEY);
        const list = stored ? JSON.parse(stored) : [];
        persistBypass(list.filter((m) => m.id !== id));
        return true;
      }

      const res = await fetch('/api/maincourante-delete', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Erreur lors de la suppression');
        return false;
      }

      await refresh();
      return true;
    },
    [refresh, persistBypass]
  );

  const markAsRead = useCallback(() => {
    if (!user) return;
    setLastSeen(user.id, new Date().toISOString());
    setUnreadCount(0);
    setLatestUnreadMessage(null);
    setUnreadMessages([]);
  }, [user]);

  return (
    <MainCouranteContext.Provider
      value={{
        messages,
        loading,
        error,
        unreadCount,
        latestUnreadMessage,
        unreadMessages,
        createMessage,
        updateMessage,
        deleteMessage,
        markAsRead,
        refresh,
      }}
    >
      {children}
    </MainCouranteContext.Provider>
  );
}
